import { useEffect, useId, useRef, type ReactNode } from "react";
import type { Team, Tournament } from "@spikeeasy/tournament";

export function Notice({
  children,
  error = false,
}: {
  children: ReactNode;
  error?: boolean;
}) {
  return (
    <div
      className={error ? "notice error" : "notice"}
      role={error ? "alert" : "status"}
    >
      {children}
    </div>
  );
}
export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
export function Dialog({
  title,
  children,
  onClose,
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  useEffect(() => {
    const previous = document.activeElement;
    const dialog = ref.current;
    dialog?.showModal();
    return () => {
      dialog?.close();
      if (previous instanceof HTMLElement) previous.focus();
    };
  }, []);
  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      className="sheet"
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <section>
        <header className="section-heading">
          <h2 id={titleId}>{title}</h2>
          <button className="icon-button" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </header>
        {children}
      </section>
    </dialog>
  );
}
export function teamLabel(team: Pick<Team, "name" | "seed">): string {
  return `${team.name} (#${team.seed})`;
}
export function teamName(t: Tournament, id: string | null | undefined): string {
  const team = t.teams.find((team) => team.id === id);
  return team ? teamLabel(team) : "To be decided";
}
export function formatTime(
  value: string | undefined,
  timezone: string,
): string {
  return value
    ? new Intl.DateTimeFormat("en-US", {
        timeZone: timezone,
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZoneName: "short",
      }).format(new Date(value))
    : "After earlier games";
}
export const modeLabels = {
  single: "Single elimination",
  double: "Double elimination",
  pools: "Pools + playoff",
  king: "King of the Court",
  swiss: "Swiss",
};
export const stageLabels = {
  pool: "Pool",
  swiss: "Swiss",
  winner: "Winners",
  loser: "Losers",
  playoff: "Playoff",
  semifinal: "Semifinal",
  final: "Final",
  bronze: "Third place",
};
